/**
 * Verifier Portfolio Management Page
 * Allows verifiers and quality inspectors to create, edit, and delete
 * portfolio items showcasing past verification/inspection work.
 *
 * Features:
 * - Photo upload for inspection evidence
 * - Full-screen media viewer for photo preview
 * - Multi-currency support (USD, ZWL, ZAR, BWP)
 * - Auto-dismiss success alerts
 *
 * Uses existing backend routes:
 *   GET    /api/verifiers/profile/:id/portfolio
 *   POST   /api/verifiers/profile/:id/portfolio
 *   PUT    /api/verifiers/profile/:id/portfolio/:itemId
 *   DELETE /api/verifiers/profile/:id/portfolio/:itemId
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Box, Typography, Button, Card, CardContent, CardMedia,
  CardActions, Grid, TextField, Dialog, DialogTitle, DialogContent,
  DialogActions, IconButton, Alert, CircularProgress, Chip, Paper,
  Select, MenuItem, FormControl, InputLabel, FormControlLabel,
  Checkbox, Divider, LinearProgress
} from '@mui/material';
import {
  Add, Delete, Edit, Save, Close, AssignmentTurnedIn,
  LocationOn, CalendarToday, AttachMoney, Visibility, VisibilityOff,
  Star, ArrowBack, PhotoCamera, ZoomIn, AutoAwesome, OpenInNew
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { formatCurrency } from '../utils/helpers';
import verifierService from '../services/verifierService';
import { brand } from '../theme/colors';
import DialogCloseButton from '../components/common/DialogCloseButton';
import MediaViewer from '../components/projects/MediaViewer';

const VERIFICATION_TYPES = [
  'Foundation Inspection',
  'Structural Assessment',
  'Roofing Inspection',
  'Plumbing Inspection',
  'Electrical Inspection',
  'Milestone Verification',
  'Material Quality Check',
  'Site Progress Audit',
  'Final Completion Inspection',
  'Safety Compliance Audit',
  'Code Compliance Review',
  'Other'
];

const improveVerifierDescriptionLocally = (description, verificationType = '') => {
  const clean = description.trim();
  const typeLabel = verificationType ? `${verificationType} assignment` : 'verification assignment';
  return `${clean}\n\nThis ${typeLabel} highlights a structured inspection process, clear evidence gathering, and practical recommendations. The work reflects professional attention to construction quality, compliance, and client confidence in the reported findings.`;
};

const PROJECT_TYPES = [
  'Residential Construction',
  'Commercial Building',
  'Renovation & Remodeling',
  'Infrastructure',
  'Industrial',
  'Multi-storey',
  'Other'
];

const CURRENCIES = [
  { code: 'USD', label: 'USD ($)' },
  { code: 'ZWL', label: 'ZWL (ZW$)' },
  { code: 'ZAR', label: 'ZAR (R)' },
  { code: 'BWP', label: 'BWP (P)' }
];

const EMPTY_ITEM = {
  title: '',
  description: '',
  projectType: '',
  verificationType: '',
  projectLocation: '',
  projectValue: '',
  currency: 'USD',
  verificationDate: '',
  clientName: '',
  clientTestimonial: '',
  isFeatured: false,
  isPublic: true
};

const VerifierPortfolioPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [profileId, setProfileId] = useState(null);
  const [portfolio, setPortfolio] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [showDialog, setShowDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState(EMPTY_ITEM);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Photo management
  const [photos, setPhotos] = useState([]);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const photoInputRef = useRef(null);

  // Media viewer
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerMedia, setViewerMedia] = useState(null);
  const [viewerAllMedia, setViewerAllMedia] = useState([]);

  // AI description improvement
  const [improvingDescription, setImprovingDescription] = useState(false);

  // Load verifier profile + portfolio on mount
  useEffect(() => {
    loadData();
  }, []);

  // Auto-dismiss success alerts after 4 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 4000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      const profile = await verifierService.getMyProfile();
      if (!profile || !profile.id) {
        setError('Verifier profile not found. Please complete onboarding first.');
        setLoading(false);
        return;
      }

      setProfileId(profile.id);

      const items = await verifierService.getPortfolio(profile.id);
      setPortfolio(Array.isArray(items) ? items : []);
    } catch (err) {
      if (err.message?.includes('not found') || err.message?.includes('404')) {
        setError('Verifier profile not found. Please complete onboarding first.');
      } else {
        setError(err.message || 'Failed to load portfolio');
      }
    } finally {
      setLoading(false);
    }
  };

  const parseImages = (images) => {
    if (!images) return [];
    if (typeof images === 'string') {
      try { return JSON.parse(images); } catch { return [images]; }
    }
    return Array.isArray(images) ? images : [];
  };

  const openAdd = () => {
    setEditingItem(null);
    setFormData(EMPTY_ITEM);
    setPhotos([]);
    setShowDialog(true);
  };

  const openEdit = (item) => {
    setEditingItem(item);
    setFormData({
      title: item.title || '',
      description: item.description || '',
      projectType: item.project_type || '',
      verificationType: item.verification_type || '',
      projectLocation: item.project_location || '',
      projectValue: item.project_value || '',
      currency: item.currency || 'USD',
      verificationDate: item.verification_date
        ? item.verification_date.split('T')[0]
        : '',
      clientName: item.client_name || '',
      clientTestimonial: item.client_testimonial || '',
      isFeatured: item.is_featured || false,
      isPublic: item.is_public !== false
    });
    setPhotos(parseImages(item.image_urls));
    setShowDialog(true);
  };

  const closeDialog = () => {
    setShowDialog(false);
    setEditingItem(null);
    setFormData(EMPTY_ITEM);
    setPhotos([]);
  };

  const readFileAsDataUrl = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const handlePhotoSelect = async (event) => {
    const files = Array.from(event.target.files);
    if (!files.length) return;

    setUploadingPhotos(true);
    const uploadedUrls = [];
    const failedFiles = [];

    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        setError(`"${file.name}" is not an image. Only photos are allowed.`);
        continue;
      }
      if (file.size > 10 * 1024 * 1024) {
        setError(`"${file.name}" is too large (max 10MB).`);
        continue;
      }

      try {
        const fd = new FormData();
        fd.append('images', file);
        fd.append('uploadedAt', new Date().toISOString());

        const response = await api.post('/uploads/portfolio', fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        if (response.data.success && response.data.data?.urls) {
          uploadedUrls.push(...response.data.data.urls);
        } else if (response.data.success && response.data.data?.url) {
          uploadedUrls.push(response.data.data.url);
        }
      } catch (uploadErr) {
        console.error(`Upload failed for ${file.name}:`, uploadErr);
        try {
          const dataUrl = await readFileAsDataUrl(file);
          uploadedUrls.push(dataUrl);
        } catch (previewErr) {
          console.error(`Fallback image encoding failed for ${file.name}:`, previewErr);
          failedFiles.push(file.name);
        }
      }
    }

    if (failedFiles.length > 0) {
      setError(`Failed to upload: ${failedFiles.join(', ')}. Please try again.`);
    }

    if (uploadedUrls.length > 0) {
      setPhotos(prev => [...prev, ...uploadedUrls]);
    }

    setUploadingPhotos(false);
    if (photoInputRef.current) photoInputRef.current.value = '';
  };

  const removePhoto = (index) => {
    setPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const handleImproveDescription = async () => {
    if (!formData.description.trim() || formData.description.trim().length < 10) {
      setError('Please write at least a short description first so AI can improve it.');
      return;
    }
    setImprovingDescription(true);
    setError('');
    try {
      const response = await api.post('/verifiers/portfolio/ai/improve-description', {
        description: formData.description,
        projectType: formData.verificationType || formData.projectType || ''
      });
      if (response.data.success) {
        setFormData(prev => ({ ...prev, description: response.data.data.improvedContent }));
      }
    } catch (err) {
      console.error('AI improve error:', err);
      setFormData(prev => ({
        ...prev,
        description: improveVerifierDescriptionLocally(
          prev.description,
          prev.verificationType || prev.projectType
        )
      }));
      setError('');
    } finally {
      setImprovingDescription(false);
    }
  };

  const handleSave = async () => {
    if (!formData.title.trim()) {
      setError('Title is required.');
      return;
    }
    if (!formData.description.trim()) {
      setError('Description is required.');
      return;
    }

    // Filter out blob URLs
    const cleanPhotos = photos.filter(url => !url.startsWith('blob:'));
    const blobCount = photos.length - cleanPhotos.length;
    if (blobCount > 0) {
      setError(`${blobCount} photo(s) failed to upload and were removed. Please re-upload them.`);
      setPhotos(cleanPhotos);
      return;
    }

    setSaving(true);
    setError('');

    try {
      const payload = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        projectType: formData.projectType,
        verificationType: formData.verificationType,
        projectLocation: formData.projectLocation.trim(),
        projectValue: formData.projectValue
          ? parseFloat(formData.projectValue)
          : null,
        currency: formData.currency || 'USD',
        verificationDate: formData.verificationDate || null,
        clientName: formData.clientName.trim(),
        clientTestimonial: formData.clientTestimonial.trim(),
        imageUrls: cleanPhotos,
        isFeatured: formData.isFeatured,
        isPublic: formData.isPublic
      };

      if (editingItem) {
        await verifierService.updatePortfolioItem(profileId, editingItem.id, payload);
        setSuccess('Portfolio item updated.');
      } else {
        await verifierService.addPortfolioItem(profileId, payload);
        setSuccess('Portfolio item added.');
      }

      closeDialog();
      await loadData();
    } catch (err) {
      setError(err.message || 'Failed to save portfolio item.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (itemId) => {
    setDeleting(true);
    setError('');
    try {
      await verifierService.deletePortfolioItem(profileId, itemId);
      setSuccess('Portfolio item deleted.');
      setDeleteConfirm(null);
      await loadData();
    } catch (err) {
      setError(err.message || 'Failed to delete portfolio item.');
    } finally {
      setDeleting(false);
    }
  };

  const getItemImages = (item) => parseImages(item.image_urls);

  const openViewer = (item) => {
    const images = getItemImages(item);
    if (images.length === 0) return;
    const allMedia = images.map((url, i) => ({
      id: `photo-${i}`,
      url,
      description: item.title,
      fileName: `Inspection photo ${i + 1}`
    }));
    setViewerMedia(allMedia[0]);
    setViewerAllMedia(allMedia);
    setViewerOpen(true);
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 6, textAlign: 'center' }}>
        <CircularProgress size={48} />
        <Typography variant="h6" sx={{ mt: 2 }}>Loading portfolio...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4, gap: 2 }}>
        <IconButton onClick={() => navigate('/dashboard')} size="small">
          <ArrowBack />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" fontWeight="bold">
            My Verification Portfolio
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Showcase your inspection and verification work to attract more clients
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<OpenInNew />}
            onClick={() => window.open(`/verifiers/${user?.id}?tab=portfolio`, '_blank')}
          >
            Preview Public Profile
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={openAdd}
            disabled={!profileId}
          >
            Add Portfolio Item
          </Button>
        </Box>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Empty state */}
      {portfolio.length === 0 && !loading && (
        <Paper
          elevation={0}
          sx={{
            p: 6,
            textAlign: 'center',
            border: '2px dashed',
            borderColor: 'divider',
            borderRadius: 3
          }}
        >
          <AssignmentTurnedIn sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            No portfolio items yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Add your past verification and inspection work to build credibility with clients.
          </Typography>
          <Button variant="contained" startIcon={<Add />} onClick={openAdd}>
            Add First Portfolio Item
          </Button>
        </Paper>
      )}

      {/* Portfolio grid */}
      <Grid container spacing={3}>
        {portfolio.map((item) => {
          const images = getItemImages(item);
          const mainImage = images[0];

          return (
            <Grid item xs={12} sm={6} md={4} key={item.id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
                {/* Image area */}
                <Box
                  sx={{ position: 'relative', cursor: images.length > 0 ? 'pointer' : 'default' }}
                  onClick={() => images.length > 0 && openViewer(item)}
                >
                  {mainImage ? (
                    <CardMedia
                      component="img"
                      height="180"
                      image={mainImage}
                      alt={item.title}
                      sx={{ objectFit: 'cover' }}
                    />
                  ) : (
                    <Box sx={{
                      height: 100,
                      bgcolor: '#f5f5f5',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <AssignmentTurnedIn sx={{ fontSize: 40, color: '#ccc' }} />
                    </Box>
                  )}

                  {images.length > 1 && (
                    <Chip
                      label={`${images.length} photos`}
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        bgcolor: 'rgba(0,0,0,0.7)',
                        color: 'white',
                        fontSize: '0.75rem'
                      }}
                    />
                  )}

                  {mainImage && (
                    <Box sx={{
                      position: 'absolute', inset: 0,
                      bgcolor: 'rgba(0,0,0,0)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      transition: 'background-color 0.3s',
                      '&:hover': { bgcolor: 'rgba(0,0,0,0.3)' },
                      '&:hover .view-icon': { opacity: 1 }
                    }}>
                      <ZoomIn className="view-icon" sx={{ fontSize: 36, color: 'white', opacity: 0, transition: 'opacity 0.3s' }} />
                    </Box>
                  )}
                </Box>

                {/* Featured badge */}
                {item.is_featured && (
                  <Chip
                    icon={<Star sx={{ fontSize: 14 }} />}
                    label="Featured"
                    size="small"
                    color="warning"
                    sx={{ position: 'absolute', top: 8, left: 8 }}
                  />
                )}

                <CardContent sx={{ flex: 1 }}>
                  {/* Public badge */}
                  {!item.is_public && (
                    <Chip
                      icon={<VisibilityOff />}
                      label="Private"
                      size="small"
                      variant="outlined"
                      sx={{ mb: 1 }}
                    />
                  )}

                  <Typography variant="h6" gutterBottom>
                    {item.title}
                  </Typography>

                  {item.verification_type && (
                    <Chip
                      label={item.verification_type}
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ mb: 1 }}
                    />
                  )}

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mb: 2,
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden'
                    }}
                  >
                    {item.description}
                  </Typography>

                  <Divider sx={{ my: 1 }} />

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {item.project_location && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <LocationOn sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">
                          {item.project_location}
                        </Typography>
                      </Box>
                    )}
                    {item.verification_date && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <CalendarToday sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">
                          {new Date(item.verification_date).toLocaleDateString()}
                        </Typography>
                      </Box>
                    )}
                    {item.project_value && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <AttachMoney sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">
                          Project value: {formatCurrency(Number(item.project_value), item.currency || 'USD')}
                        </Typography>
                      </Box>
                    )}
                    {item.client_name && (
                      <Typography variant="caption" color="text.secondary">
                        Client: {item.client_name}
                      </Typography>
                    )}
                  </Box>

                  {item.client_testimonial && (
                    <Paper
                      variant="outlined"
                      sx={{ p: 1.5, mt: 2, bgcolor: 'grey.50', borderRadius: 1 }}
                    >
                      <Typography variant="caption" sx={{ fontStyle: 'italic' }}>
                        "{item.client_testimonial}"
                      </Typography>
                    </Paper>
                  )}
                </CardContent>

                <CardActions sx={{ justifyContent: 'flex-end', pt: 0 }}>
                  <IconButton size="small" onClick={() => openEdit(item)} title="Edit">
                    <Edit fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => setDeleteConfirm(item)}
                    title="Delete"
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Add/Edit dialog */}
      <Dialog
        open={showDialog}
        onClose={closeDialog}
        maxWidth="md"
        fullWidth
        disableEscapeKeyDown={saving}
      >
        <DialogCloseButton onClick={closeDialog} />
        <DialogTitle>
          {editingItem ? 'Edit Portfolio Item' : 'Add Portfolio Item'}
        </DialogTitle>

        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., Structural inspection — 4-storey commercial building, Harare"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Verification Type</InputLabel>
                <Select
                  value={formData.verificationType}
                  label="Verification Type"
                  onChange={(e) =>
                    setFormData({ ...formData, verificationType: e.target.value })
                  }
                >
                  {VERIFICATION_TYPES.map((t) => (
                    <MenuItem key={t} value={t}>{t}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Project Type</InputLabel>
                <Select
                  value={formData.projectType}
                  label="Project Type"
                  onChange={(e) =>
                    setFormData({ ...formData, projectType: e.target.value })
                  }
                >
                  {PROJECT_TYPES.map((t) => (
                    <MenuItem key={t} value={t}>{t}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                multiline
                rows={4}
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe the scope of the inspection, what you assessed, findings, and outcome..."
                inputProps={{ maxLength: 2000 }}
                helperText={`${formData.description.length}/2000`}
              />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={improvingDescription ? <CircularProgress size={16} color="inherit" /> : <AutoAwesome />}
                  onClick={handleImproveDescription}
                  disabled={improvingDescription || !formData.description.trim()}
                  sx={{
                    background: brand.gradient,
                    textTransform: 'none',
                    fontWeight: 'bold',
                    '&:hover': {
                      background: brand.gradientHover
                    }
                  }}
                >
                  {improvingDescription ? 'Improving...' : 'Improve with AI'}
                </Button>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Location"
                value={formData.projectLocation}
                onChange={(e) =>
                  setFormData({ ...formData, projectLocation: e.target.value })
                }
                placeholder="e.g., Harare CBD"
                InputProps={{ startAdornment: <LocationOn sx={{ mr: 1, color: 'text.secondary' }} /> }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Verification Date"
                type="date"
                value={formData.verificationDate}
                onChange={(e) =>
                  setFormData({ ...formData, verificationDate: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={4} sm={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Currency</InputLabel>
                <Select
                  value={formData.currency}
                  onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                  label="Currency"
                >
                  {CURRENCIES.map(c => (
                    <MenuItem key={c.code} value={c.code}>{c.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={8} sm={4}>
              <TextField
                fullWidth
                label="Project Value"
                type="number"
                value={formData.projectValue}
                onChange={(e) =>
                  setFormData({ ...formData, projectValue: e.target.value })
                }
                inputProps={{ min: 0, step: 1000 }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Client Name"
                value={formData.clientName}
                onChange={(e) =>
                  setFormData({ ...formData, clientName: e.target.value })
                }
                placeholder="Optional — e.g., ABC Developers"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Client Testimonial"
                value={formData.clientTestimonial}
                onChange={(e) =>
                  setFormData({ ...formData, clientTestimonial: e.target.value })
                }
                placeholder="Optional — paste a quote from the client about your service"
              />
            </Grid>

            {/* Inspection Photos */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Inspection Photos (Optional)
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Upload photos from your inspections to showcase your work quality
              </Typography>

              <input
                ref={photoInputRef}
                type="file"
                accept="image/*"
                multiple
                style={{ display: 'none' }}
                onChange={handlePhotoSelect}
              />

              {photos.length > 0 && (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                  {photos.map((url, index) => (
                    <Box key={index} sx={{ position: 'relative', width: 80, height: 80 }}>
                      <Box
                        component="img"
                        src={url}
                        sx={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 1 }}
                      />
                      <IconButton
                        size="small"
                        sx={{
                          position: 'absolute', top: -8, right: -8,
                          bgcolor: 'error.main', color: 'white', width: 20, height: 20,
                          '&:hover': { bgcolor: 'error.dark' }
                        }}
                        onClick={() => removePhoto(index)}
                      >
                        <Close sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              )}

              {uploadingPhotos && (
                <Box sx={{ mb: 1 }}>
                  <LinearProgress sx={{ borderRadius: 2 }} />
                  <Typography variant="caption" color="text.secondary">
                    Uploading photos...
                  </Typography>
                </Box>
              )}

              <Button
                variant="outlined"
                size="small"
                startIcon={<PhotoCamera />}
                onClick={() => photoInputRef.current?.click()}
                disabled={uploadingPhotos}
              >
                {photos.length > 0 ? 'Add More Photos' : 'Upload Photos'}
              </Button>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.isFeatured}
                    onChange={(e) =>
                      setFormData({ ...formData, isFeatured: e.target.checked })
                    }
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Star fontSize="small" sx={{ color: formData.isFeatured ? '#f59e0b' : 'text.secondary' }} />
                    Feature this item (shown first on profile)
                  </Box>
                }
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.isPublic}
                    onChange={(e) =>
                      setFormData({ ...formData, isPublic: e.target.checked })
                    }
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {formData.isPublic ? <Visibility fontSize="small" /> : <VisibilityOff fontSize="small" />}
                    {formData.isPublic ? 'Visible to clients' : 'Private (hidden)'}
                  </Box>
                }
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={closeDialog} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || uploadingPhotos || !formData.title.trim() || !formData.description.trim()}
            startIcon={saving ? <CircularProgress size={18} /> : <Save />}
          >
            {saving ? 'Saving...' : editingItem ? 'Save Changes' : 'Add to Portfolio'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog
        open={!!deleteConfirm}
        onClose={() => !deleting && setDeleteConfirm(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogCloseButton onClick={() => !deleting && setDeleteConfirm(null)} />
        <DialogTitle>Delete Portfolio Item?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>"{deleteConfirm?.title}"</strong>? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => handleDelete(deleteConfirm.id)}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={18} /> : <Delete />}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Media Viewer */}
      <MediaViewer
        open={viewerOpen}
        onClose={() => setViewerOpen(false)}
        media={viewerMedia}
        allMedia={viewerAllMedia}
      />
    </Container>
  );
};

export default VerifierPortfolioPage;
